import { makeAutoObservable, runInAction } from 'mobx';
import {
  adminPaymentSchemeTemplatesCreate,
  adminPaymentSchemeTemplatesDelete,
  adminPaymentSchemeTemplatesGetList,
  adminPaymentSchemeTemplatesGetOne,
  adminPaymentSchemeTemplatesPropagate,
  adminPaymentSchemeTemplatesUpdate,
} from '@/services/employeesApi';
import { AppError } from '@/utils/errors';
import type {
  CreatePaymentSchemeTemplatePayload,
  PaymentSchemeTemplate,
  PaymentSchemeTemplateListItem,
  PropagationResult,
  UpdatePaymentSchemeTemplatePayload,
} from '@/types/employee.types';
import { toastStore } from './ToastStore';

/**
 * Шаблоны схем оплаты: список для Настроек и для селектов (карточка сотрудника,
 * модалка применения шаблона), CRUD, архивация, propagate-превью при сохранении.
 */
class PaymentSchemeTemplatesStore {
  items: PaymentSchemeTemplateListItem[] = [];
  total = 0;
  isLoading = false;
  isSaving = false;
  submitError: string | null = null;

  activeTemplate: PaymentSchemeTemplate | null = null;
  isLoadingTemplate = false;
  lastPropagation: PropagationResult | null = null;

  private serviceCenterUuid: string | null = null;
  private loadTemplateRequestId = 0;

  constructor() {
    makeAutoObservable(this);
  }

  setServiceCenterUuid = (serviceCenterUuid: string) => {
    if (this.serviceCenterUuid === serviceCenterUuid) return;
    this.serviceCenterUuid = serviceCenterUuid;
    this.fetchList();
  };

  fetchList = async (includeArchived = false) => {
    if (!this.serviceCenterUuid) return;

    this.isLoading = true;
    try {
      const response = await adminPaymentSchemeTemplatesGetList({
        service_center_uuid: this.serviceCenterUuid,
        include_archived: includeArchived,
        limit: 200,
      });
      runInAction(() => {
        this.items = response.data ?? [];
        this.total = response.total ?? 0;
        this.isLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError('Не удалось загрузить шаблоны оплаты');
    }
  };

  get activeOptions() {
    return this.items
      .filter((template) => !template.is_archived)
      .map((template) => ({ value: template.uuid, label: template.name }));
  }

  loadTemplateDetail = async (uuid: string) => {
    this.isLoadingTemplate = true;
    const requestId = ++this.loadTemplateRequestId;
    try {
      const template = await adminPaymentSchemeTemplatesGetOne(uuid);
      if (requestId !== this.loadTemplateRequestId) return;
      runInAction(() => {
        this.activeTemplate = template;
        this.isLoadingTemplate = false;
      });
    } catch {
      if (requestId !== this.loadTemplateRequestId) return;
      runInAction(() => {
        this.isLoadingTemplate = false;
      });
      toastStore.showError('Не удалось загрузить шаблон');
    }
  };

  clearActiveTemplate = () => {
    this.loadTemplateRequestId += 1;
    this.activeTemplate = null;
    this.lastPropagation = null;
    this.submitError = null;
  };

  createTemplate = async (payload: CreatePaymentSchemeTemplatePayload): Promise<boolean> => {
    this.isSaving = true;
    this.submitError = null;
    try {
      await adminPaymentSchemeTemplatesCreate(payload);
      runInAction(() => {
        this.isSaving = false;
      });
      toastStore.showSuccess('Шаблон создан');
      await this.fetchList();
      return true;
    } catch (error) {
      runInAction(() => {
        this.isSaving = false;
        this.submitError = error instanceof AppError ? error.message : 'Не удалось создать шаблон';
      });
      return false;
    }
  };

  // ВАЖНО: PATCH сам по себе меняет только определение шаблона — он не
  // каскадирует изменения на сотрудников (для этого отдельный эндпоинт
  // propagate). Раньше это предполагалось "так работает бэкенд" и propagate
  // нигде не вызывался — правки шаблона реально не долетали до сотрудников.
  // Пролив нужно запускать явно после успешного сохранения — см. propagateTemplate.
  updateTemplate = async (
    uuid: string,
    payload: UpdatePaymentSchemeTemplatePayload
  ): Promise<boolean> => {
    this.isSaving = true;
    this.submitError = null;
    try {
      const updated = await adminPaymentSchemeTemplatesUpdate(uuid, payload);
      runInAction(() => {
        this.isSaving = false;
        this.activeTemplate = updated;
      });
      await this.fetchList();
      return true;
    } catch (error) {
      runInAction(() => {
        this.isSaving = false;
        this.submitError = error instanceof AppError ? error.message : 'Не удалось сохранить шаблон';
      });
      return false;
    }
  };

  previewPropagation = async (uuid: string) => {
    try {
      const preview = await adminPaymentSchemeTemplatesPropagate(uuid, true);
      runInAction(() => {
        this.lastPropagation = preview;
      });
    } catch {
      toastStore.showError('Не удалось получить превью применения шаблона');
    }
  };

  /** Реально каскадирует уже сохранённый шаблон на всех назначенных сотрудников. */
  propagateTemplate = async (uuid: string): Promise<boolean> => {
    try {
      const result = await adminPaymentSchemeTemplatesPropagate(uuid, false);
      runInAction(() => {
        this.lastPropagation = result;
      });
      toastStore.showSuccess(
        result.updated_count > 0
          ? `Шаблон сохранён и применён к сотрудникам (${result.updated_count})`
          : 'Шаблон сохранён'
      );
      return true;
    } catch {
      toastStore.showError('Шаблон сохранён, но не удалось применить изменения к сотрудникам. Повторите позже.');
      return false;
    }
  };

  archiveTemplate = async (uuid: string, isArchived: boolean): Promise<boolean> => {
    try {
      await adminPaymentSchemeTemplatesUpdate(uuid, { is_archived: isArchived });
      toastStore.showSuccess(isArchived ? 'Шаблон скрыт из выбора' : 'Шаблон возвращён в список');
      await this.fetchList(true);
      return true;
    } catch {
      toastStore.showError('Не удалось изменить статус шаблона');
      return false;
    }
  };

  deleteTemplate = async (uuid: string): Promise<boolean> => {
    try {
      await adminPaymentSchemeTemplatesDelete(uuid);
      toastStore.showSuccess('Шаблон удалён');
      await this.fetchList();
      return true;
    } catch (error) {
      if (error instanceof AppError && error.statusCode === 409) {
        toastStore.showError('Нельзя удалить шаблон — он назначен сотрудникам');
      } else {
        toastStore.showError('Не удалось удалить шаблон');
      }
      return false;
    }
  };
}

export const paymentSchemeTemplatesStore = new PaymentSchemeTemplatesStore();
export type { PaymentSchemeTemplatesStore };
